import {
  useEffect,
  useRef,
  useState,
  useCallback,
  Dispatch,
  SetStateAction,
  ChangeEvent,
} from 'react';
import { Control, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  usePublishPostMutation,
  PostListItem,
  useEditPostMutation,
  CategoryListItemExt,
  useUsersListQuery,
  useCategoriesListQuerywithVisbility,
} from 'api';
import { FileItem } from 'api/types';
import { alert } from 'components';
import { ATTACHMENTS_LIMIT, QUERIES } from 'constants/index';
import { useCallbackPrompt, useFormAttachments } from 'hooks';
import i18n from 'i18n';
import {
  Attachment,
  CategoriesListType,
  CreatePostFormValues,
  DocumentsType,
  PostDraft,
  SelectOption,
  User,
  UserStatus,
} from 'types';
import { objectsDifferences } from 'utils';
import { isSystemCategory } from 'validations';

import { defaultValuesAdapter, formValuesAdapter } from '../adapters';
import { AllOption } from '../constants';

interface SelectionType {
  code: string;
  label: string;
  group: string;
}

interface UseCreatePostFormReturn {
  handleFormSubmit: () => void;
  control: Control<CreatePostFormValues>;
  isLoading: boolean;
  handleAddImages: (files: FileList, category_ids?: string[], post_id?: string) => Promise<void>;
  handleAddDocuments: (files: FileList, category_ids?: string[], post_id?: string) => Promise<void>;
  handleAddVideos: (files: FileList, category_ids?: string[], post_id?: string) => Promise<void>;
  attachedImages: Attachment[];
  attachedVideos: Attachment[];
  attachedDocs: Attachment[];
  handleDeleteAttachment: (attachmentId: string, type: 'images' | 'docs' | 'videos') => void;
  handleCloseButton: () => void;
  handleCloseModal: () => void;
  handleSubmitConfirmModal: () => void;
  selectedPlaces: SelectionType[];
  setSelectedPlaces: Dispatch<SetStateAction<SelectionType[]>>;
  setFirstChange: Dispatch<SetStateAction<boolean>>;
  handleAssigneeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleAssigneeDefault: (myProfile: string, userProfile?: string) => void;
  handleCheckboxChange: () => void;
  handlePinCheckboxChange: () => void;
  handleSelectGalleryFiles: (files: FileItem[]) => void;
  notification: boolean;
  pin: boolean;
  categoryMap: CategoriesListType;
  usersList: User[];
  categoriesOptionsList: SelectOption[];
  isOpen: boolean;
  isDirty: boolean;
  progress: number;
  buttonDisable: boolean;
  isNotDisableButton: boolean;
  isBlockedRemoveAttachments: boolean;
  isPublicDocument: boolean;
}

interface UseCreatePostFormProps {
  draft?: PostDraft;
  document?: PostListItem;
  isEdit?: boolean;
  onClose?: () => void;
}

export function useCreateDocumentForm({
  draft,
  document,
  isEdit,
  onClose,
}: UseCreatePostFormProps): UseCreatePostFormReturn {
  const postId = (draft?.id || document?.id) as string;
  const [isSuccess, setIsSuccess] = useState(false);
  const defaultValues = defaultValuesAdapter({ draft, document });
  const [selectedPlaces, setSelectedPlaces] = useState<SelectionType[]>([]);
  const [notification, setNotification] = useState<boolean>(false);
  const [pin, setPin] = useState<boolean>(false);
  const [galleryFileIds, setGalleryFileIds] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_firstChange, setFirstChange] = useState(false);
  const prevProfileIdsRef = useRef<string[]>([]);

  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, isDirty, isSubmitted, errors },
  } = useForm<CreatePostFormValues>({
    defaultValues,
    mode: 'onBlur',
  });

  const navigate = useNavigate();
  const { isLoading: isUsersListLoading, usersList } = useUsersListQuery({
    status: [UserStatus.Active, UserStatus.Registered],
  });

  const visibility = true;
  const {
    categoryMap,
    categoriesList,
    isLoading: isCategoryListLoading,
  }: {
    categoriesList: CategoryListItemExt[];
    categoryMap: CategoriesListType;
    isLoading: boolean;
  } = useCategoriesListQuerywithVisbility(visibility);

  const attachedImages = watch('images');
  const attachedDocs = watch('docs');
  const attachedVideos = watch('videos');
  const documentType = watch('level');
  const filesChanges = objectsDifferences(
    { images: attachedImages, docs: attachedDocs, videos: attachedVideos },
    {
      images: defaultValues.images,
      docs: defaultValues.docs,
      videos: defaultValues.videos,
    },
  );
  const countOfAttachments = attachedImages.length + attachedDocs.length + attachedVideos.length;
  const { showPrompt, confirmNavigation, cancelNavigation } = useCallbackPrompt(
    ((isDirty || Object.keys(filesChanges).length > 0) && !isSubmitted) ||
      Object.keys(errors).length > 0,
  );

  const categoriesOptionsList = categoriesList
    .filter((category) => category.tag && !isSystemCategory(category.tag))
    .map((category) => {
      return {
        value: category?.id || '',
        label: category.title,
        icon: category.icon,
        tag: category.tag,
      };
    });

  const { publishPostMutation, isLoading: isPublishPostLoading } = usePublishPostMutation({
    successText: t('toasts.document.success.create'),
    setIsSuccess,
    invalidateQueries: [QUERIES.LAST_UPDATED_DOCS, QUERIES.DOCUMENTS_LIST],
  });

  const { editPostMutation, isLoading: isEditPostLoading } = useEditPostMutation({
    successText: t('toasts.document.success.edit'),
    setIsSuccess,
    invalidateQueries: [QUERIES.LAST_UPDATED_DOCS, QUERIES.DOCUMENTS_LIST],
  });

  const {
    handleAddImages,
    handleAddDocuments,
    handleAddVideos,
    handleDeleteAttachment,
    isLoadingFileUpload,
    progress,
  } = useFormAttachments({
    setFormValue: setValue,
    attachedDocs,
    attachedImages,
    attachedVideos,
    isEdit,
    currentPostId: postId,
  });

  const handleCheckboxChange = () => {
    setNotification((prev) => !prev);
  };

  const handlePinCheckboxChange = () => {
    setPin((prev) => !prev);
  };

  function handleSelectGalleryFiles(files: FileItem[]) {
    const IMAGE_TYPES = ['image/png', 'image/gif', 'image/jpeg', 'image/webp', 'image'];
    const VIDEO_TYPES = [
      'video/mp4',
      'video/mkv',
      'video/quicktime',
      'video/avi',
      'video/x-flv',
      'video/mpeg',
      'video/webm',
      'video/3gpp',
      'video',
    ];

    const toAttachment = (f: FileItem): Attachment => ({
      id: f.id,
      display_name: f.display_name,
      type: IMAGE_TYPES.includes(f.type)
        ? 'image'
        : VIDEO_TYPES.includes(f.type)
        ? 'video'
        : 'document',
      file_extension: f.file_extension,
      file: { small: '', medium: '', large: '', original: f.display_name },
      file_url: {
        small: f.file_url?.small ?? '',
        medium: f.file_url?.medium ?? '',
        large: f.file_url?.large ?? '',
        original: f.file_url?.original ?? '',
        video_uid: f.file_url?.video_uid ?? '',
      },
    });

    const existingIds = new Set(
      [...attachedImages, ...attachedDocs, ...attachedVideos].map((a) => a.id),
    );
    const newFiles = files.filter((f) => !existingIds.has(f.id));

    const newImages = newFiles.filter((f) => IMAGE_TYPES.includes(f.type)).map(toAttachment);
    const newVideos = newFiles.filter((f) => VIDEO_TYPES.includes(f.type)).map(toAttachment);
    const newDocs = newFiles
      .filter((f) => !IMAGE_TYPES.includes(f.type) && !VIDEO_TYPES.includes(f.type))
      .map(toAttachment);
    const existingAttachementCount = existingIds.size;
    const attachmentsCount =
      newImages.length + newVideos.length + newDocs.length + existingAttachementCount;

    if (attachmentsCount > ATTACHMENTS_LIMIT) {
      alert.error(i18n.t('toasts.attachmentsLimit'));
    } else {
      if (newImages.length) setValue('images', [...attachedImages, ...newImages]);
      if (newVideos.length) setValue('videos', [...attachedVideos, ...newVideos]);
      if (newDocs.length) setValue('docs', [...attachedDocs, ...newDocs]);
    }
  }

  const handleAssigneeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e?.target?.value;
      if (!!value && value.length > 1) {
        if (value?.at(-1) === AllOption.value) {
          setValue('visibility', [AllOption.value]);
        } else if (value.includes(AllOption.value)) {
          setValue(
            'visibility',
            (value as unknown as string[]).filter((value) => value !== AllOption.value),
          );
        }
      }
    },
    [setValue],
  );

  const handleAssigneeDefault = useCallback(
    (myProfile: string, userProfile?: string) => {
      if (myProfile && !userProfile) {
        setValue('visibility', [myProfile]);
      }
      if (myProfile && userProfile) {
        setValue('visibility', [myProfile, userProfile]);
      }
    },
    [setValue],
  );

  const handleFormSubmit = handleSubmit(async ({ visibility, ...data }) => {
    const file_ids = [
      ...[...attachedImages, ...attachedDocs, ...attachedVideos].map((file) => file.id),
      ...galleryFileIds,
    ];
    const { title, description } = data;
    const itemsToRemove = ['all', 'managers'];

    const visibility_ids = visibility?.filter((item) => !itemsToRemove.includes(item)) || [];

    const newsfeed = selectedPlaces.some((i) => i.code === 'newsfeed');
    const tags = selectedPlaces
      .filter((place) => place?.group === t('post.create.form.placeGroups.categories'))
      .map((place) => place?.code);
    const profiles: string[] =
      selectedPlaces
        .filter((place) => place?.group === t('post.create.form.placeGroups.profiles'))
        .map((place) => place?.code) || [];

    let group_visibility;
    switch (true) {
      case visibility?.length === 0:
        group_visibility = 'no';
        break;
      case visibility?.length === 1 && visibility[0] === 'managers':
        group_visibility = 'managers';
        break;
      case profiles?.length && visibility?.length && visibility.includes('managers'):
        group_visibility = 'managers';
        break;
      case newsfeed && !!tags?.length:
        group_visibility = 'custom';
        break;
      case visibility?.length === 1 && visibility[0] === 'all':
        group_visibility = 'all';
        break;
      default:
        group_visibility = 'custom';
    }

    const adaptedData = {
      post: formValuesAdapter({
        title,
        description,
        type: 'docs',
        tags,
        newsfeed,
        profiles,
        group_visibility,
        assigned_to: visibility_ids,
        notify: notification,
        pin,
        file_ids,
        visibility: visibility_ids,
      }),
      postId: postId,
    };

    if (isEdit) {
      await editPostMutation(adaptedData);
      setGalleryFileIds([]);
      return;
    }
    await publishPostMutation(adaptedData);
    setGalleryFileIds([]);
  });

  function handleCloseButton() {
    onClose ? onClose() : navigate(-1);
  }

  async function handleSubmitConfirmModal() {
    confirmNavigation();
    onClose ? onClose() : navigate(-1);
  }

  useEffect(() => {
    if (document) {
      const visible_to = document?.visible_to?.map((i) => i?.id) || [];
      if (document?.notify) {
        setNotification(true);
      }
      if (document?.pin) {
        setPin(true);
      }
      if (document?.visibility === 'custom') {
        setValue('visibility', [...visible_to]);
      }
      if (document?.visibility === 'managers') {
        setValue('visibility', ['managers', ...visible_to]);
      }
      if (document?.visibility === 'all') {
        setValue('visibility', ['all']);
      }
      setValue('level', document.level);
    }
  }, [document, setValue, setNotification]);

  useEffect(() => {
    if (document) {
      const newsfeedEntries: { code: string; label: string; group: string }[] = document?.newsfeed
        ? [
            {
              code: 'newsfeed',
              label: t('post.create.form.placeGroups.newsfeed'),
              group: t('post.create.form.placeGroups.newsfeed'),
            },
          ]
        : [];
      const existCategories =
        document?.category_tags?.map((tag) => ({
          code: tag.id,
          label: categoriesList.find((cL) => cL.id === tag.id)?.title || tag.title || '',
          group: t('post.create.form.placeGroups.categories'),
        })) || [];
      const profile =
        document?.assigned_to?.map((user) => ({
          code: user?.id || '',
          label: `${user?.first_name}${user?.last_name}`,
          group: t('post.create.form.placeGroups.profiles'),
        })) || [];
      setSelectedPlaces([...newsfeedEntries, ...existCategories, ...profile]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document]);

  useEffect(() => {
    if (isSubmitted && Object.keys(errors).length === 0 && isSuccess) {
      onClose ? onClose() : navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted, navigate, errors, attachedImages, attachedDocs, attachedVideos, isSuccess]);

  const titleData = watch('title');
  const visibilities = watch('visibility');

  useEffect(() => {
    const isCategoryExist = selectedPlaces?.some(
      (item) => item?.group === t('post.create.form.placeGroups.categories'),
    );
    const isProfileExist = selectedPlaces?.some(
      (item) => item?.group === t('post.create.form.placeGroups.profiles'),
    );
    const profileIds = selectedPlaces
      .filter((sp) => sp.group === t('post.create.form.placeGroups.profiles'))
      .map((sp) => sp.code);

    if (isCategoryExist && !isProfileExist && visibilities?.length) {
      setValue(
        'visibility',
        visibilities.filter((v) => v === AllOption.value),
      );
      prevProfileIdsRef.current = [];
      return;
    }

    if (isProfileExist || prevProfileIdsRef.current.length > 0) {
      const removedProfileIds = new Set(
        prevProfileIdsRef.current.filter((id) => !profileIds.includes(id)),
      );
      const currentVisibility = visibilities || [];
      const withoutRemoved = currentVisibility.filter((v) => !removedProfileIds.has(v));
      const merged = [...new Set([...profileIds, ...withoutRemoved])];
      setValue('visibility', merged);
      prevProfileIdsRef.current = profileIds;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlaces, setValue]);

  const checkCategory = selectedPlaces?.some(
    (item) => item?.group === t('post.create.form.placeGroups.categories'),
  );

  const buttonDisable =
    !selectedPlaces?.length || !titleData?.length || (!checkCategory && !visibilities?.length);

  return {
    control,
    handleFormSubmit,
    isLoading:
      isSubmitting ||
      isPublishPostLoading ||
      isEditPostLoading ||
      isLoadingFileUpload ||
      isUsersListLoading ||
      isCategoryListLoading,
    handleAddDocuments,
    handleAddVideos,
    handleAddImages,
    attachedImages,
    attachedVideos,
    attachedDocs,
    handleDeleteAttachment,
    handleCloseButton,
    handleCloseModal: cancelNavigation,
    isOpen: showPrompt,
    handleSubmitConfirmModal,
    handleAssigneeChange,
    handleAssigneeDefault,
    selectedPlaces,
    setSelectedPlaces,
    setFirstChange,
    handleCheckboxChange,
    handlePinCheckboxChange,
    handleSelectGalleryFiles,
    notification,
    pin,
    usersList,
    categoryMap,
    isDirty,
    categoriesOptionsList,
    progress,
    buttonDisable,
    isNotDisableButton: isDirty,
    isBlockedRemoveAttachments: Boolean(countOfAttachments === 1 && isEdit),
    isPublicDocument: documentType === DocumentsType.PUBLIC,
  };
}
